import Markdown from 'markdown-to-jsx';
import React from 'react';
import styled from 'styled-components';
import useSWR from 'swr';
import { getUserId } from '@core/auth/redux';
import { media } from '@core/styles/breakpoints';
import { track } from '@engage/analytics/actions';
import { white, slate3, black } from '@engage/colors/base';
import { useReduxAction, useReduxState } from '@engage/redux/hooks';
import { spaces } from '@engage/styles/spaces';
import { title24, body16 } from '@engage/typography/font-styles';
import { button1Styles, largeButtonStyles } from '@members/buttons/buttons';
import { SettingsCopy, useSettings } from '@members/copy/copy.generated';
import { Settings } from '@members/copy/models.generated/Settings';
import { UsersFetchers } from '@members/data-fetch';
import { tooltipsApi } from '@members/data-fetch/singletons';
import { useFeatureToggle } from '@members/feature-toggles/featureToggles';
import { Feature } from '@members/feature-toggles/toggles';
import { useMembersRouter as useRouter } from '@members/localization/useMembersRouter';
import { isPathAllowed, Modal } from '@members/modal/Modal';
import { useIsBreakpointLessThan } from '@members/responsive/hooks';
import { trackViewedPrivacyPreferencesModal } from '../../analytics';
import { setTooltipSeen } from '../../api';
import { useTooltipsData } from '../../hooks/useTooltipsData';

const tooltipId = 'vppa_onboarding_tooltip';

const PrivacyPreferencesModal: React.FC<React.PropsWithChildren<unknown>> = () => {
  const router = useRouter();
  const isMobile = useIsBreakpointLessThan('tablet');
  const userId = useReduxState(getUserId);
  const privacyUpdatesEnabled = useFeatureToggle(Feature.Vppa);
  const { data: seenTooltip, mutate } = useTooltipsData('vppa_onboarding_tooltip');
  const showTooltip = seenTooltip === false && privacyUpdatesEnabled;
  const [isOpen, setOpen] = React.useState(false);
  const trackAnalytics = useReduxAction(track);
  const { data: user, isValidating } = useSWR(
    ...UsersFetchers.GetUser({
      id: userId,
      pelotonPlatform: 'web',
    }),
  );

  const shouldShowModal = !!user && isPathAllowed(window.location.pathname);

  const onClose = async () => {
    setOpen(false);
    if (userId) {
      await setTooltipSeen(tooltipsApi, userId, tooltipId);
      mutate();
    }
  };

  React.useEffect(() => {
    if (!isValidating && shouldShowModal && showTooltip) {
      setOpen(true);
      trackAnalytics(trackViewedPrivacyPreferencesModal());
    }
  }, [shouldShowModal, isValidating, showTooltip]);

  return (
    <Modal
      isOpen={isOpen}
      closeHandler={() => onClose()}
      style={toOverriddenStyles(isMobile)}
      contentLabel="PrivacyPreferences"
    >
      <BackgroundContainer>
        <ContentContainer>
          <Title data-test-id="vppaToolTipHeader">
            <SettingsCopy id={Settings.VppaPelotonPrivacyReminder} />
          </Title>
          <Description data-test-id="vppaToolTipDetails">
            <Markdown options={{ overrides: { a: PrivacyPolicyLink } }}>
              {useSettings(Settings.VppaAccessCopy, {
                privacyLinkText: useSettings(Settings.VppaPrivacyPolicy),
              })}
            </Markdown>
            <br />
            <br />
            <Markdown
              options={{ overrides: { a: PrivacySettingsLink, b: PrivacyCenterLink } }}
            >
              {useSettings(Settings.VppaMembersCopy, {
                privacySettingsText: useSettings(Settings.VppaPrivacySettings),
                privacyCenterText: useSettings(Settings.VppaPrivacyCenter),
              })}
            </Markdown>
          </Description>
        </ContentContainer>
        <DoneButtonContainer>
          <DoneButton
            data-test-id="vppaToolTipCta"
            onClick={() => {
              onClose();
              router.push('/preferences/settings');
            }}
          >
            <SettingsCopy id={Settings.VppaCTACopy} />
          </DoneButton>
        </DoneButtonContainer>
      </BackgroundContainer>
    </Modal>
  );
};

const BackgroundContainer = styled.div`
  width: 100%;
  height: 100%;
  margin: auto;
  background-color: ${white};
  display: flex;
  flex-direction: column;
  text-align: center;
  align-items: center;
  z-index: 4;
  padding: 32px;

  ${media.tablet`
    width: 480px;
    border-radius: ${spaces.small}px;
    max-height: 675px;

    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  `}
  color: ${slate3};
`;

const ContentContainer = styled.div`
  ${media.tablet`
    overflow: scroll;
    overflow-x: hidden;
  `}
`;

const Title = styled.h1`
  ${title24};
  margin-bottom: ${spaces.small}px;
`;

const Description = styled.h1`
  ${body16};
  font-weight: 500;
`;

const DoneButtonContainer = styled.div`
  background: linear-gradient(rgba(255, 255, 255, 0), ${white});
  border-radius: 0 0 ${spaces.small}px ${spaces.small}px;
  position: relative;
`;

const DoneButton = styled.button`
  ${button1Styles}
  ${largeButtonStyles}
  width: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${spaces.xxxLarge}px auto 0px auto;
`;

const tabletStyleOverrides = {
  content: {
    maxWidth: '520px',
  },
};

const toOverriddenStyles = (isMobile: boolean) => {
  if (isMobile) {
    return {};
  }

  return tabletStyleOverrides;
};

const PrivacyPolicyLink: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <StyledLink href="https://www.onepeloton.com/privacy-policy">{children}</StyledLink>
);

const PrivacySettingsLink: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => (
  <StyledLink href="https://members.onepeloton.com/preferences/settings">
    {children}
  </StyledLink>
);

const PrivacyCenterLink: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => (
  <StyledLink href="https://www.onepeloton.com/privacy-center">{children}</StyledLink>
);

const StyledLink = styled.a`
  letter-spacing: normal;
  color: ${black};
  text-decoration: underline;
`;

export default PrivacyPreferencesModal;
